import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Pagination from "@/components/feature/Pagination";
import { useSeo } from "@/hooks/useSeo";
import { newsCategories, newsList } from "@/mocks/news";

const PAGE_SIZE = 6;

export default function News() {
  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);

  useSeo({
    title: "仙剑奇侠传资讯专题 - 剧情解读与角色访谈",
    description:
      "仙剑奇侠传资讯专题，汇聚剧情解读、角色访谈、幕后花絮与仙侠行业动态，帮你深入了解每一部经典仙侠作品。",
    keywords: "仙剑奇侠传资讯,仙侠剧剧情解读,角色访谈,仙侠剧幕后花絮,仙侠影视动态",
  });

  const filtered = useMemo(() => {
    const list =
      category === "全部" ? newsList : newsList.filter((item) => item.category === category);
    return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleCategory = (next: string) => {
    setCategory(next);
    setPage(1);
  };

  const tabs = ["全部", ...newsCategories];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">资讯专题</span>
            </nav>
            <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              资讯专题
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              剧情解读、角色访谈、幕后花絮与行业动态，记录仙剑系列与经典仙侠剧的每一个精彩瞬间。
            </p>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => handleCategory(tab)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  category === tab
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:border-primary-400"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="mt-8 flex flex-col gap-4">
                {pageItems.map((item) => (
                  <article
                    key={item.id}
                    className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row sm:p-5"
                  >
                    <div className="h-44 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-32 sm:w-52">
                      <img
                        src={item.image}
                        alt={item.title}
                        title={`${item.title} 资讯配图`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-800">
                          {item.category}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-foreground-400">
                          <i className="ri-calendar-line"></i>
                          {item.date}
                        </span>
                        <span className="hidden items-center gap-1 text-xs text-foreground-400 sm:flex">
                          <i className="ri-user-line"></i>
                          {item.author}
                        </span>
                      </div>
                      <h2 className="mt-2.5 font-heading text-base font-bold leading-snug text-foreground-900 group-hover:text-primary-700 md:text-lg">
                        {item.title}
                      </h2>
                      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                        {item.excerpt}
                      </p>
                      <div className="mt-auto pt-3">
                        <Link
                          to={`/detail/${item.relatedSeriesId}`}
                          className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:underline"
                        >
                          阅读相关剧集
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
              <Pagination current={safePage} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                <i className="ri-article-line text-2xl"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
                该分类暂无资讯
              </h3>
              <p className="mt-2 max-w-sm text-sm text-foreground-600">
                换个分类看看，或者回到全部资讯浏览最新内容。
              </p>
              <button
                type="button"
                onClick={() => handleCategory("全部")}
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-list-check"></i>
                查看全部资讯
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}